import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { PagePrint, CustomizeProductHeader, Loading, Button } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { FullscreenModal } from '@/components/global'
import { FontLoadWarningModal } from '@/components/editor/FontLoadWarningModal/FontLoadWarningModal'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useFontLoadWarning } from '@/hooks/editor/useFontLoadWarning'
import { resetOrderProduct } from '@/api/orderProducts'
import { userTypes } from '@messenger/consts'
import { userAtom } from '@/atoms/_globalAtoms'
import * as S from './Package.styled'
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil'
import {
  orderAtom,
  selectedProductsSelector,
  layoutAtom,
  dateFormatAtom,
  applicationTypeAtom,
  currentProductIdAtom,
} from 'atoms'
import {
  pageSetLocaleAtom,
  serviceInformationTemplatesAtom,
} from '@/atoms/_editorAtoms'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { useSetToast } from 'hooks'
import funeralHomeDateFormat from 'utils/dates/funeralHomeDateFormat'
import { EventCategory, logger } from 'utils/logger'
import { loggerSentry } from '@/utils/loggerSentry'
import { isOrderPageTurnedOverError } from '@/utils/editor/orderPageTurnedOver'
import { useTranslation } from 'react-i18next'
import { getFuneralHomeSettings } from '@/api/funeralHomeSettings'
import { getThemeOverride, getWatermark } from '@/api/themes'
import { updateOrderProduct } from '@/api/orderProducts'
import {
  getEditorOrderPageDetails,
  getOrderPageDetails,
  updateOrderPage,
} from '@/api/orderPages'
import {
  ensureOrderPageSets,
  getOrderPageSets,
  getOrderPageSetsById,
} from '@/api/orderPageSets'
import { LOCALE } from '@messenger/consts'
import { combinesPages } from '@/utils/editor/combinesPages'
import { getServiceInformationTemplates } from '@/api/serviceInformationTemplates'

export const CustomizeProduct = ({
  productInfo,
  themeInfo,
  productsAndLayouts,
  getOrderProducts,
  pdfState,
  updatePdfState,
  isTransitioning = false,
}) => {
  const handleError = useErrorHandler('CustomizeProduct')
  const setToast = useSetToast()
  const { t } = useTranslation()
  const currentProductIdRef = useRef(null)
  const [selectedProducts, setSelectedProducts] = useRecoilState(
    selectedProductsSelector,
  )
  const [layout, setLayout] = useState('')
  const [productStatus, setProductStatus] = useState('')
  const [selectedLayout, setSelectedLayout] = useState('')
  const [allLayouts, setAllLayouts] = useState([])
  const [pageSet, setPageSet] = useState(null)
  const order = useRecoilValue(orderAtom)
  const currentProductId = useRecoilValue(currentProductIdAtom)
  const setDateFormat = useSetRecoilState(dateFormatAtom)
  const [pagesData, setPagesData] = useState([])
  // The product layout, which the preview's own page details lack: its
  // margins, which bound Lotus box growth and feed the bleed report, and its
  // folds, which decide whether a page set that asks for the combined page
  // gets it (a trifold never does).
  const [previewLayout, setPreviewLayout] = useState(null)
  const [themeOverride, setThemeOverride] = useState(null)
  const [storedLayout, setStoredLayout] = useRecoilState(layoutAtom)
  const [isLoading, setIsLoading] = useState(true)
  const [concatenatedPages, setConcatenatedPages] = useState(0)
  const isConcatenatedPages = combinesPages(concatenatedPages, previewLayout)
  const setPageSetLocale = useSetRecoilState(pageSetLocaleAtom)
  const applicationType = useRecoilValue(applicationTypeAtom)
  const setServiceTemplates = useSetRecoilState(serviceInformationTemplatesAtom)
  const fontLoadWarning = useFontLoadWarning()

  const getSettings = async () => {
    try {
      const { data } = await getFuneralHomeSettings()
      const date = funeralHomeDateFormat(data?.date_format)
      setDateFormat(date)
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const loadPreviewLayout = async (orderPageSetId) => {
    try {
      const { data } = await getEditorOrderPageDetails(
        orderPageSetId,
        applicationType,
      )
      setPreviewLayout(data?.layout ?? null)
    } catch (error) {
      // The preview paints without its margins: Lotus boxes grow with no
      // margin bound, the bleed report is lost, and a trifold page set that
      // asks for the combined page is drawn combined.
      setPreviewLayout(null)
      logger.warn({
        message: 'preview_layout_margins_unavailable',
        category: EventCategory.ERROR,
        data: { orderPageSetId, error: error?.message },
      })
    }
  }

  const getPageData = async (pages) => {
    try {
      //sort the pages
      let pagesJson = []

      const fetchedWatermarks = await getThemeOverrides()

      pages?.forEach((page) => {
        if (!page.json_template) return

        if (page.json_template.product) {
          page.json_template.product.orderPageId = page.id
        }

        pagesJson.push(page.json_template)
      })

      pagesJson.sort((a, b) => {
        return a.pageProperties.pageNumber > b.pageProperties.pageNumber
          ? 1
          : -1
      })

      let matchedMarks
      //if the json template has watermarks and is page 1 (I think interior pages do not have watermarks 🤔)
      pagesJson = pagesJson.map((page) => {
        if (
          page?.product?.watermarks?.length > 0 &&
          page.pageProperties.pageNumber === 1
        ) {
          //check to see if the currently set watermarks match the incoming watermarks
          //we want to see if these are the same watermarks as on the currently selected theme
          //we'll want to set some values in the future to track if they replaced the watermarks (then the ids won't match but we won't want to update them)
          //TODO - create logic for handling replaced watermarks and replaced 1 watermark
          matchedMarks = fetchedWatermarks.find((mark) => {
            return page.product.watermarks.find(
              (storedMark) => storedMark.id === mark.id,
            )
          })
        }
        //if there are no matches (the watermarks are new to this package) and it's page 1, set the watermarks to be the ones fetched from the db
        if (!matchedMarks && page.pageProperties.pageNumber === 1) {
          page.product.watermarks = fetchedWatermarks
          //if they never go into the editor we need to add the watermarks to the json so it'll print on the pdf, but we dont need to wait for that.
          const payload = {
            json_template: JSON.stringify(page),
            save_reason: 'watermark_sync',
          }

          updateOrderPage(
            page.product.orderPageId,
            payload,
            applicationType,
          ).catch((error) => {
            // The page set was turned over after this preview loaded it: the
            // stored page is newer than this copy, so the sync skips it.
            if (isOrderPageTurnedOverError(error)) {
              logger.info({
                message: 'preview_watermark_sync_skipped_page_turned_over',
                category: EventCategory.SAVE,
                data: { orderPageId: page.product.orderPageId },
              })
              return
            }
            handleError(error?.response?.data?.message || error.message, error)
          })
        }
        return page
      })

      setPagesData(pagesJson)
      const concatPages = pagesJson[0]?.pageProperties?.concatenatedPages
      if (concatPages) {
        setConcatenatedPages(concatPages)
      }
      setIsLoading(false)
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  // Returns `true` when the page-set fetch + rebuild succeeded, `false`
  // when an error was caught and surfaced via `handleError`. Callers (e.g.
  // `handleResetLayout`) use this flag to gate success toasts so a silent
  // failure during the rebuild step doesn't show a misleading "Reset"
  // confirmation when the layout is still broken.
  const getPageSets = async () => {
    try {
      // Language resolution now happens server-side (per line, order
      // fallback, then EN) in the ensure endpoint; this warn keeps visibility
      // on lines that would silently fall back to EN.
      if (order?.id && !productInfo?.language && !order?.language) {
        loggerSentry.error({
          message: 'order_language_undefined_in_get_page_sets',
          category: EventCategory.ERROR,
          data: {
            orderId: order?.id,
            productId: productInfo?.id,
            fallback: LOCALE.EN,
          },
        })
      }

      const { data: orderPageSetsData } = await getOrderPageSets(
        productInfo.id, //this is the order product ID now
        applicationType,
      )
      const isOrderCreated = orderPageSetsData.length > 0

      if (isOrderCreated) {
        const sortedSets = orderPageSetsData.sort((a, b) => {
          if (a.default && !b.default) return -1
          if (!a.default && b.default) return 1
          const aDate = new Date(a.updated_at || a.created_at)
          const bDate = new Date(b.updated_at || b.created_at)
          return bDate - aDate
        })
        let defaultOrderPageSet = sortedSets[0]
        const { current_order_page_set_id_used, id } = defaultOrderPageSet

        const hasSameOrderPageSetId = current_order_page_set_id_used === id
        const orderPageSetIdsDontMatch =
          current_order_page_set_id_used && !hasSameOrderPageSetId

        if (orderPageSetIdsDontMatch) {
          logger.info({
            message:
              'preview_page_set_id_does_not_match_current_order_page_set_id_used',
            category: EventCategory.DISCREPANCY,
            data: {
              ...orderPageSetsData,
              ...sortedSets,
            },
          })

          const { data: orderPageSetsByIdData } = await getOrderPageSetsById(
            current_order_page_set_id_used,
            applicationType,
          )

          const isOrderPageSetsByIdEmpty = orderPageSetsByIdData.length === 0

          if (isOrderPageSetsByIdEmpty) {
            throw new Error(
              'There is not a default layout for this product. Contact technical support to receive help!',
            )
          }

          defaultOrderPageSet = orderPageSetsByIdData[0]
        }
        setPageSet(defaultOrderPageSet)
        void loadPreviewLayout(defaultOrderPageSet.id)

        setPageSetLocale(defaultOrderPageSet?.language || LOCALE.EN)

        const { data: orderPagesData } = await getOrderPageDetails(
          defaultOrderPageSet.id,
          applicationType,
        )
        getPageData(orderPagesData)
      } else {
        // No page set yet: one backend call bootstraps the whole order — the
        // server resolves each product's default template (FH first, global
        // fallback, language-matched) and creates page sets + pages for every
        // product in parallel. Replaces the old template-fetch/pick/create
        // round-trip chain, and warms the other products' page sets so
        // switching tabs never re-enters this branch.
        if (!order?.id) {
          throw new Error(
            'There is not a default layout for this product. Contact technical support to receive help!',
          )
        }
        const { data: ensureData } = await ensureOrderPageSets(
          order.id,
          applicationType,
        )

        const productResult = ensureData.results.find(
          (result) => result.order_product_id === productInfo.id,
        )

        if (!productResult?.ok || !productResult.page_set) {
          throw new Error(
            productResult?.error ||
              'There is not a default layout for this product. Contact technical support to receive help!',
          )
        }

        const newPageSetData = productResult
        setPageSetLocale(newPageSetData.page_set?.language || LOCALE.EN)
        setPageSet(newPageSetData.page_set)
        if (newPageSetData.page_set?.id)
          void loadPreviewLayout(newPageSetData.page_set.id)
        getPageData(newPageSetData.pages)
      }
      return true
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
      return false
    }
  }

  const getThemeOverrides = async () => {
    try {
      const { data } = await getThemeOverride(
        themeInfo.theme_id || themeInfo.id,
        selectedLayout.layout_id, // this is the product id now
        applicationType,
      )
      if (data.length === 0) return
      setThemeOverride(data[0])

      const fetchedWatermarks = []
      if (data?.[0].first_watermark) {
        const { data: firstWatermark } = await getWatermark(
          data[0].first_watermark,
        )
        //set the required data fields and do the math to translate them to 96dpi
        //TODO: once the old site is decommissioned we can run a script to do this globally
        firstWatermark.type = 'watermark'
        firstWatermark.y = (firstWatermark.top / 72) * 96
        firstWatermark.x = (firstWatermark.left / 72) * 96
        firstWatermark.height = (firstWatermark.height / 72) * 96
        firstWatermark.width = (firstWatermark.width / 72) * 96
        firstWatermark.z = 1
        //add the watermark to the array of watermarks
        fetchedWatermarks.push(firstWatermark)
      }
      if (data?.[0].second_watermark) {
        const { data: secondWatermark } = await getWatermark(
          data[0].second_watermark,
        )
        secondWatermark.type = 'watermark'
        secondWatermark.y = (secondWatermark.top / 72) * 96
        secondWatermark.x = (secondWatermark.left / 72) * 96
        secondWatermark.z = 1
        secondWatermark.height = (secondWatermark.height / 72) * 96
        secondWatermark.width = (secondWatermark.width / 72) * 96

        fetchedWatermarks.push(secondWatermark)
      }

      return fetchedWatermarks
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const setLayoutAndPersist = (layout) => {
    setLayout(layout)
    setStoredLayout(layout)
  }

  const setAndUpdateProductStatus = async (_productStatus) => {
    if (!productInfo?.id || _productStatus.trim() === productStatus.trim())
      return

    try {
      await updateOrderProduct(
        productInfo.id,
        { status: _productStatus },
        applicationType,
      )
      getOrderProducts()
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const determineLayout = (
    currentProductId,
    storedLayout,
    productsAndLayouts,
  ) => {
    if (!currentProductId) {
      console.warn('determineLayout: currentProductId is empty or invalid.')
      return
    }

    // Find product by ID instead of name to support duplicate product names
    const storedProduct = productsAndLayouts.find(
      (product) => (product.id || product.orderProductId) === currentProductId,
    )

    if (storedProduct) {
      const storedProductHasLayouts =
        storedProduct.layouts && storedProduct.layouts.length > 0
      if (storedProductHasLayouts) {
        const layoutExists = storedProduct.layouts.find(
          (l) => l.id === storedLayout,
        )
        if (layoutExists) {
          setLayout(storedLayout)
        } else {
          setLayout(storedProduct.layouts[0].id)
          setStoredLayout(storedProduct.layouts[0].id)
        }
      } else {
        const warningMessage = t('toast.noLayoutsDefined', {
          name: storedProduct.name,
        })
        console.warn(warningMessage)
        setToast({
          text: warningMessage,
          icon: 'error',
        })
      }
    } else {
      const warningMessage = t('toast.productNotFound', {
        tab: currentProductId,
        name: productInfo?.name,
      })
      console.warn(warningMessage)
      setToast({
        text: warningMessage,
        icon: 'error',
      })
      const productInfoHasLayouts = productInfo?.layouts?.length > 0
      if (productInfoHasLayouts) {
        setLayout(productInfo.layouts[0].id)
        setStoredLayout(productInfo.layouts[0].id)
      } else {
        const errorMessage = t('toast.fallbackNoLayouts', {
          name: productInfo?.name,
        })
        console.error(errorMessage)
        setToast({
          text: errorMessage,
          icon: 'error',
        })
      }
    }
  }

  const getServiceTemplates = useCallback(
    async (productId) => {
      // Check if this request is still valid (productId hasn't changed)
      if (currentProductIdRef.current !== productId) {
        return
      }

      try {
        if (!productId) {
          if (currentProductIdRef.current === productId) {
            setServiceTemplates([])
          }
          return
        }

        const { data: serviceTemplates } =
          await getServiceInformationTemplates(productId)

        // Only update state if productId hasn't changed since request started
        if (currentProductIdRef.current === productId) {
          setServiceTemplates(serviceTemplates)
        }
      } catch (error) {
        // Only handle error if productId hasn't changed
        if (currentProductIdRef.current === productId) {
          handleError(error?.response?.data?.message || error.message, error)
          // Clear templates on error to prevent stale data
          setServiceTemplates([])
        }
      }
    },
    [handleError, setServiceTemplates],
  )

  useEffect(() => {
    const foundLayout = allLayouts.find((item) => item.id === layout)
    if (foundLayout) {
      setSelectedLayout(foundLayout)
    }
  }, [allLayouts, layout])

  useEffect(() => {
    // Use currentProductId, falling back to productInfo.id
    const productId =
      currentProductId || productInfo?.id || productInfo?.orderProductId
    if (productId && productsAndLayouts.length > 0) {
      determineLayout(productId, storedLayout, productsAndLayouts)
    }
    //eslint-disable-next-line
  }, [currentProductId, productsAndLayouts, productInfo])

  useEffect(() => {
    const shouldGetPageSets = selectedLayout?.id && themeInfo?.id
    if (shouldGetPageSets) {
      setIsLoading(true)
      getPageSets()
    }
    // Reload when the effective language changes (per-line override or the
    // order language) so a mid-session language switch re-fetches templates
    // instead of leaving stale pages on screen.
    //eslint-disable-next-line
  }, [selectedLayout, themeInfo, productInfo?.language, order?.language])

  useEffect(() => {
    if (!productInfo?.id) {
      setServiceTemplates([])
      currentProductIdRef.current = null
      return
    }

    setAllLayouts(productInfo.layouts)
    setProductStatus(productInfo.status)

    // Use consistent property access - check product_id like the API expects
    const productId = productInfo.product_id

    // Update ref to track current productId
    currentProductIdRef.current = productId

    getServiceTemplates(productId)

    // Cleanup function to mark this productId as invalid
    return () => {
      currentProductIdRef.current = null
    }
    //eslint-disable-next-line
  }, [productInfo, getServiceTemplates])

  useEffect(() => {
    getSettings()
    //eslint-disable-next-line
  }, [])

  const oldProduct = !isLoading && pagesData?.length < 1
  const isAdmin = useIsAdmin()
  // `useIsAdmin()` includes `actAsAdmin` (an arbitrary user with the flag
  // flipped on by support). `POST /v1/orderProducts/:id/reset` admits only a
  // caller who may act for the line's order, and its role gate,
  // `auth('superAdmin')`, will admit only SUPER_ADMIN and CORPORATION_ADMIN
  // sessions once #3541 enforces it; until then it refuses edit links and
  // only logs a signed-in session it would refuse. The
  // button is shown to those two types alone, the sessions the gate is meant
  // to admit.
  const resetEligibleUser = useRecoilValue(userAtom)
  const canResetLayout = useMemo(
    () =>
      isAdmin &&
      (resetEligibleUser?.type === userTypes.superAdmin ||
        resetEligibleUser?.type === userTypes.corporationAdmin),
    [isAdmin, resetEligibleUser?.type],
  )
  const [isResetting, setIsResetting] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)

  // Admin-only recovery. Deletes all OrderPageSets for this orderProduct via
  // the BE; the follow-up calls re-fire the same fetch sequence that runs
  // when a product is first selected in the editor — so the layout attempts
  // to recreate from the active template. Used when the preview shows the
  // "this product does not have a default layout set up correctly" error
  // (typically an orphan OPS from the historical creation-race bug).
  const handleResetLayout = async () => {
    if (!productInfo?.id) return
    try {
      setIsResetting(true)
      await resetOrderProduct(productInfo.id, applicationType)

      // Re-fire the page-set fetch chain so it attempts to recreate the
      // layout exactly like opening the product in the editor for the first
      // time: clear local state, flip loading on, refresh parent's product
      // list (which propagates the new OrderProduct row), then explicitly
      // call getPageSets() — the no-existing-OPS branch creates a fresh OPS
      // from the active template.
      setPagesData([])
      setPageSet(null)
      setIsLoading(true)
      if (typeof getOrderProducts === 'function') {
        await getOrderProducts()
      }
      // `getPageSets()` swallows its own errors via `handleError` and now
      // returns a success flag so we can gate the success toast on the
      // actual rebuild outcome — a silent failure after deleting the page
      // sets must NOT show a misleading "Reset" confirmation.
      const didRebuildSucceed = await getPageSets()

      if (didRebuildSucceed) {
        setToast({
          text: t('package.resetLayoutSuccess', {
            defaultValue: 'Layout reset. Rebuilding from template…',
          }),
        })
        setShowResetModal(false)
      }
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    } finally {
      setIsResetting(false)
      // Safety net: `getPageSets()` swallows its own errors internally and
      // only clears `isLoading` on the happy path (inside `getPageData`).
      // Without this finally-block reset, a silent failure during the heal
      // chain would leave the spinner stuck on indefinitely.
      setIsLoading(false)
    }
  }

  return (
    <Flex column>
      <CustomizeProductHeader
        order={order}
        productInfo={productInfo}
        themeInfo={themeInfo}
        selectedLayout={[selectedLayout, setSelectedLayout]}
        selectedProducts={[selectedProducts, setSelectedProducts]}
        pageSet={pageSet}
        allLayouts={allLayouts}
        layout={[layout, setLayoutAndPersist]}
        productStatus={[productStatus, setAndUpdateProductStatus]}
        oldProduct={oldProduct}
        isLoading={isLoading}
        getOrderProducts={getOrderProducts}
        pdfState={pdfState}
        updatePdfState={updatePdfState}
        applicationType={applicationType}
      />

      <S.RenderContainer
        column
        border="1px solid"
        borderColor="gray4"
        radius="4px"
        margin="0 auto 40px auto"
        maxHeight="65vh"
        height="650px"
        width="1000px"
        maxWidth="70vw"
        background="gray5"
        position="relative"
        justify={(isLoading || isTransitioning) && 'center'}
        align={(isLoading || isTransitioning) && 'center'}
      >
        <Loading isLoading={isLoading || isTransitioning}>
          {pagesData?.length > 0 && (
            <S.TransformWrapper>
              {!isConcatenatedPages ? (
                pagesData.map((page, index) => (
                  <PagePrint
                    key={index}
                    page={page}
                    theme={themeInfo}
                    themeOverride={themeOverride}
                    index={index}
                    pagePosition={index + 1}
                    margin={index === 0 ? '24px 0 24px 0' : '0 0 24px 0'}
                    backgroundType={productInfo.background_type}
                    baseLayout={previewLayout}
                    orderId={order?.id}
                    orderPageSetId={pageSet?.id}
                    pageSetId={pageSet?.page_set_id}
                    print
                  />
                ))
              ) : (
                <PagePrint
                  key={pagesData[0]?.pageProperties?.page_set_id}
                  page={pagesData[0]}
                  secondPage={pagesData[1]}
                  theme={themeInfo}
                  themeOverride={themeOverride}
                  index={pagesData[0]?.pageProperties?.page_set_id}
                  margin={'24px 0 24px 0'}
                  backgroundType={productInfo.background_type}
                  baseLayout={previewLayout}
                  orderId={order?.id}
                  orderPageSetId={pageSet?.id}
                  pageSetId={pageSet?.page_set_id}
                  print
                />
              )}
            </S.TransformWrapper>
          )}
        </Loading>
        {oldProduct && (
          <S.NoProduct column justify="center">
            <Text as="h3" margin="20px">
              This product does not have a default layout set up correctly.
            </Text>
            <Text margin="20px">
              Please create a new print package or contact support.
            </Text>
            {canResetLayout && (
              <Button
                primary
                onClick={() => setShowResetModal(true)}
                disabled={isResetting}
                margin="0 20px 20px"
              >
                {isResetting
                  ? t('package.resetLayoutBusy', {
                      defaultValue: 'Resetting…',
                    })
                  : t('package.resetLayout', {
                      defaultValue: 'Reset Layout (Admin)',
                    })}
              </Button>
            )}
          </S.NoProduct>
        )}
      </S.RenderContainer>

      {canResetLayout && (
        <FullscreenModal
          show={[showResetModal, () => setShowResetModal(false)]}
          hideClose={false}
        >
          <Flex column full padding="32px" maxWidth="600px">
            <Text size="20px" weight="600" margin="10px 0 10px 0">
              {t('package.resetLayoutModalTitle', {
                defaultValue: 'Reset Layout?',
              })}
            </Text>
            <Text size="16px" margin="10px 0 10px 0">
              {t('package.resetLayoutModalBody', {
                defaultValue:
                  'This deletes all page sets for this product and rebuilds them from the active template. Any unsaved customizations on this product will be lost. This cannot be undone.',
              })}
            </Text>
            <Flex justify="flex-end" margin="10px 0 0 0" gap="8px">
              <Button
                onClick={() => setShowResetModal(false)}
                disabled={isResetting}
              >
                {t('package.resetLayoutCancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                primary
                destructive
                onClick={handleResetLayout}
                disabled={isResetting}
              >
                {isResetting
                  ? t('package.resetLayoutBusy', {
                      defaultValue: 'Resetting…',
                    })
                  : t('package.resetLayoutConfirm', {
                      defaultValue: 'Reset Layout',
                    })}
              </Button>
            </Flex>
          </Flex>
        </FullscreenModal>
      )}
      <FontLoadWarningModal
        show={fontLoadWarning.show}
        families={fontLoadWarning.families}
        onClose={fontLoadWarning.close}
      />
    </Flex>
  )
}

export default CustomizeProduct
